import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useShiftPlusOffer } from "@/components/FirstMonthOffer";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, Sparkles } from "lucide-react";
import { toast } from "sonner";

const DISCORD_INVITE = process.env.REACT_APP_DISCORD_INVITE_URL || "https://discord.gg/rGcCp8hHD";

const TIERS = [
  {
    key: "free",
    name: "The Community",
    price: "Free",
    tag: "Enter here",
    features: [
      "Full Discord community access",
      "11 public channels",
      "Weekly community conversations",
      "Ask Tay open threads",
      "The 7-question Shift Reader",
    ],
    cta: "Join the Discord",
    discord: true,
  },
  {
    key: "frequency_reset",
    name: "7-Day Frequency Reset",
    price: "$33",
    tag: "One-time",
    features: ["7 daily transmissions", "Frequency journal prompts", "Lifetime access"],
    cta: "Reset my frequency",
  },
  {
    key: "timeline_workbook",
    name: "Timeline Shift Workbook",
    price: "$55",
    tag: "One-time",
    features: ["Deep-dive digital workbook", "Timeline mapping exercises", "Decision-making protocol"],
    cta: "Shift my timeline",
  },
  {
    key: "thirty_day_shift",
    name: "The 30-Day Shift",
    price: "$88",
    tag: "One-time",
    features: ["Daily 30-day protocol", "Video + audio teachings", "Weekly integration prompts", "Lifetime access"],
    cta: "Begin the 30 days",
  },
  {
    key: "shift_society",
    name: "Shift+ Membership",
    price: "$33",
    period: "/mo",
    tag: "Membership",
    highlight: true,
    features: [
      "Everything in Community, plus:",
      "Private Shift+ Discord channels",
      "Monthly live Q&A with Tay",
      "Exclusive teachings, videos & eBooks",
      "Members-only events",
    ],
    cta: "Upgrade to Shift+",
  },
  {
    key: "reprogram_reality",
    name: "Reprogram Your Reality",
    price: "$199",
    tag: "Deep-dive",
    features: ["Advanced identity protocol", "8-week structured curriculum", "Case studies + Q&A archive"],
    cta: "Go deeper",
  },
];

export default function Membership() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [activeTier, setActiveTier] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const offer = useShiftPlusOffer();
  const [params] = useSearchParams();

  const openCheckout = (tier) => {
    setEmail(user?.email || "");
    setName(user?.name || "");
    setActiveTier(tier);
  };

  useEffect(() => {
    const plan = TIERS.find((t) => t.key === params.get("plan") && !t.discord && !t.to);
    if (plan) openCheckout(plan);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params, user]);

  const isOffer = (t) => offer && t?.key === "shift_society" && !user?.is_member && !user?.member_until;

  const startCheckout = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await api.post("/square/checkout", {
        product_key: activeTier.key,
        email,
        name: name || null,
        origin_url: window.location.origin,
      });
      // Save reference so we can poll status on return
      const reference = r.data.reference;
      const checkoutUrl = r.data.checkout_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        toast.error("Couldn't get a checkout URL. Please try again.");
        setSubmitting(false);
      }
      // Note: we don't nav manually — Square redirects back to /checkout/{reference} after payment
      void reference;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 sm:pt-24" data-testid="membership-page">
      <section className="mx-auto max-w-7xl px-5 sm:px-6 py-10 sm:py-16">
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="eyebrow mb-4 flex items-center justify-center gap-2">
            <Sparkles className="h-3 w-3 text-accent" /> The Shift Room
          </div>
          <h1 className="font-serif text-[2.5rem] leading-[1.05] sm:text-6xl sm:leading-tight mb-5 sm:mb-6">
            Move from <span className="italic">insight</span> to{" "}
            <span className="italic text-accent">practice</span>.
          </h1>
          <p className="text-muted-foreground text-base sm:text-lg leading-relaxed">
            The reading is a mirror. The room is a rhythm. Secure checkout with card, Apple Pay, Google Pay, or Cash App.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {TIERS.map((t, i) => (
            <motion.div
              key={t.name}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.05 }}
              className={`border p-6 sm:p-8 flex flex-col ${
                t.highlight ? "border-accent bg-accent/5 relative" : "border-border"
              }`}
              data-testid={`tier-card-${t.key}`}
            >
              {t.highlight && (
                <div className="absolute -top-3 left-6 sm:left-8 bg-accent text-accent-foreground font-mono text-[10px] tracking-widest uppercase px-3 py-1">
                  Most beloved
                </div>
              )}
              <div className="eyebrow mb-3">{t.tag}</div>
              <h3 className="font-serif text-2xl sm:text-3xl mb-3 leading-snug">{t.name}</h3>
              <div className="mb-5 sm:mb-6 flex items-baseline gap-1">
                <span className="font-serif text-4xl sm:text-5xl">{t.price}</span>
                {t.period && (
                  <span className="font-mono text-xs text-muted-foreground">{t.period}</span>
                )}
              </div>
              {isOffer(t) && (
                <div className="-mt-3 mb-5 font-mono text-[10px] sm:text-xs tracking-widest uppercase text-accent" data-testid="shiftplus-offer-note">
                  New members: first month ${offer.shift_plus_first_month_usd}
                </div>
              )}
              <ul className="space-y-3 mb-6 sm:mb-8 flex-1">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="h-4 w-4 text-accent shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {t.discord ? (
                <a href={DISCORD_INVITE} target="_blank" rel="noopener noreferrer">
                  <Button
                    variant="outline"
                    className="w-full btn-pill font-mono uppercase text-xs tracking-widest h-12"
                    data-testid={`tier-cta-${t.key}`}
                  >
                    {t.cta}
                  </Button>
                </a>
              ) : t.to ? (
                <Link to={t.to}>
                  <Button
                    variant="outline"
                    className="w-full btn-pill font-mono uppercase text-xs tracking-widest h-12"
                    data-testid={`tier-cta-${t.key}`}
                  >
                    {t.cta}
                  </Button>
                </Link>
              ) : (
                <Button
                  onClick={() => openCheckout(t)}
                  className="w-full btn-pill font-mono uppercase text-xs tracking-widest h-12"
                  variant={t.highlight ? "default" : "outline"}
                  data-testid={`tier-cta-${t.key}`}
                >
                  {t.cta}
                </Button>
              )}
            </motion.div>
          ))}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-8 font-mono tracking-widest uppercase">
          Access delivered within 24 hours of payment confirmation
        </p>
      </section>

      <Dialog open={!!activeTier} onOpenChange={(o) => !o && setActiveTier(null)}>
        <DialogContent className="max-w-md w-[calc(100%-2rem)]" data-testid="cashapp-checkout-dialog">
          <DialogHeader>
            <div className="eyebrow mb-2">Secure checkout</div>
            <DialogTitle className="font-serif text-2xl leading-snug">
              {activeTier?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
            We'll email your access to <span className="font-mono text-foreground">{email || "your email"}</span> once payment clears. You'll pay on the next screen — card, Apple/Google Pay, or Cash App.
          </p>
          <div className="space-y-3">
            <div>
              <label className="eyebrow block mb-2">Email</label>
              <Input
                type="email"
                inputMode="email"
                autoComplete="email"
                placeholder="you@shifting.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="h-12"
                data-testid="checkout-email-input"
              />
            </div>
            <div>
              <label className="eyebrow block mb-2">Name (optional)</label>
              <Input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="h-12"
                data-testid="checkout-name-input"
              />
            </div>
            <Button
              onClick={startCheckout}
              disabled={submitting}
              className="w-full btn-pill font-mono uppercase text-xs tracking-widest h-12"
              data-testid="checkout-start-btn"
            >
              {submitting ? "Opening checkout..." : `Continue to checkout — ${isOffer(activeTier) ? `$${offer.shift_plus_first_month_usd} first month` : `${activeTier?.price}${activeTier?.period || ""}`}`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
